import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

// ── ALL THUNKS FIRST ──────────────────────────────────────────────────────

export const fetchInscriptionsByApprenant = createAsyncThunk(
  "inscriptions/fetchByApprenant",
  async ({ apprenantId, page = 1, limit = 20, append = false }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/inscriptions/apprenant/${apprenantId}?page=${page}&limit=${limit}`);
      return { data, append, page };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const fetchInscriptionsEnAttenteByFormateur = createAsyncThunk(
  "inscriptions/fetchEnAttenteByFormateur",
  async (formateurId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/inscriptions/formateur/${formateurId}/en-attente`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur chargement");
    }
  }
);

export const inscrire = createAsyncThunk(
  "inscriptions/inscrire",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post("/inscriptions", formData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur inscription");
    }
  }
);

export const abandonner = createAsyncThunk(
  "inscriptions/abandonner",
  async ({ apprenantId, formationId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/inscriptions/abandonner/${apprenantId}/${formationId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur abandon");
    }
  }
);

export const accepterInscription = createAsyncThunk(
  "inscriptions/accepter",
  async (inscriptionId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/inscriptions/${inscriptionId}/accepter`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur acceptation");
    }
  }
);

export const refuserInscription = createAsyncThunk(
  "inscriptions/refuser",
  async (inscriptionId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/inscriptions/${inscriptionId}/refuserInscription`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Erreur refus");
    }
  }
);

// ── SLICE ─────────────────────────────────────────────────────────────────

const inscriptionSlice = createSlice({
  name: "inscriptions",
  initialState: { list: [], pending: [], loading: false, error: null, success: null, page: 1, hasMore: true },
  reducers: {
    clearInscriptionMessages: (state) => { state.error = null; state.success = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInscriptionsByApprenant.pending,   (state) => { state.loading = true; })
      .addCase(fetchInscriptionsByApprenant.fulfilled, (state, action) => {
        state.loading = false;
        const { data, append, page } = action.payload;
        const list = Array.isArray(data) ? data : (data.data || []);
        if (append) {
          state.list = [...state.list, ...list];
        } else {
          state.list = list;
        }
        state.page = page;
        state.hasMore = Array.isArray(data) ? list.length >= 20 : (data?.page || 1) < (data?.pages || 1);
      })
      .addCase(fetchInscriptionsByApprenant.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchInscriptionsEnAttenteByFormateur.pending,   (state) => { state.loading = true; })
      .addCase(fetchInscriptionsEnAttenteByFormateur.fulfilled, (state, action) => {
        state.loading = false;
        state.pending = Array.isArray(action.payload) ? action.payload : (action.payload.data || []);
      })
      .addCase(fetchInscriptionsEnAttenteByFormateur.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(inscrire.fulfilled, (state, action) => { state.list.push(action.payload); state.success = "Inscription réussie."; })
      .addCase(inscrire.rejected,  (state, action) => { state.error = action.payload; })

      .addCase(abandonner.fulfilled, (state, action) => {
        const idx = state.list.findIndex((i) => i._id === action.payload._id);
        if (idx !== -1) state.list[idx] = action.payload;
        state.success = "Formation abandonnée.";
      })

      .addCase(accepterInscription.fulfilled, (state, action) => {
        state.pending = state.pending.filter((i) => i._id !== action.payload._id);
        state.success = "Inscription acceptée.";
      })
      .addCase(refuserInscription.fulfilled, (state, action) => {
        state.pending = state.pending.filter((i) => i._id !== action.payload._id);
        state.success = "Demande refusée.";
      });
  },
});

export const { clearInscriptionMessages } = inscriptionSlice.actions;
export default inscriptionSlice.reducer;
